import { Alert, Box, Collapse, Divider, Grid, Icon, Paper } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import SpacedGridContainer from "~/components/SpacedGridContainer";
import SpacedGridItem from "~/components/SpacedGridItem";
import SelectCard from "~/components/select/SelectCard";
import { BodyTextSmall, CaptionText, SubtitleText, TitleText } from "~/components/Typographies";
import { timestampFormatDateTime } from "~/helpers/timestampFormat";
import { useAppDispatch, useAppSelector } from "~/hooks/reduxHooks";
import { ErrorDetail, setErrorsViewed } from "~/store/errorSlice";

const monoBlockSx = {
	bgcolor: "action.hover",
	borderRadius: 1,
	fontFamily: "monospace",
	fontSize: "0.8125rem",
	m: 0,
	mt: 0.5,
	overflowX: "auto",
	p: 1,
	whiteSpace: "pre-wrap",
	wordBreak: "break-word"
} as const;

const formatValue = (value: ErrorDetail["detail"] | ErrorDetail["extra"]) =>
	String(value) === "[object Object]" ? JSON.stringify(value, null, 2) : String(value);

interface ErrorDetailSectionProps {
	label: string;
	value: string;
}

const ErrorDetailSection = ({ label, value }: ErrorDetailSectionProps) => (
	<Box sx={{ mb: 1.5 }}>
		<CaptionText bold color="text.secondary" sx={{ display: "block" }}>
			{label}
		</CaptionText>
		<Box component="pre" sx={monoBlockSx}>
			{value}
		</Box>
	</Box>
);

const ErrorDetailView = ({ error }: { error: ErrorDetail }) => {
	const { t } = useTranslation("common");

	return (
		<Box sx={{ mt: 1 }}>
			<ErrorDetailSection label={t("errorLog.fields.message")} value={error.message || "--"} />
			{error.code && <ErrorDetailSection label={t("errorLog.fields.code")} value={error.code} />}
			<ErrorDetailSection label={t("errorLog.fields.detail")} value={formatValue(error.detail)} />
			{error.extra && <ErrorDetailSection label={t("errorLog.fields.extra")} value={formatValue(error.extra)} />}
		</Box>
	);
};

interface ErrorLogProps {
	hosted?: boolean;
}

const ErrorLog = ({ hosted = false }: ErrorLogProps) => {
	const { t } = useTranslation("common");
	const dispatch = useAppDispatch();
	const errors = useAppSelector((state) => state.error.errors);
	const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

	useEffect(() => {
		dispatch(setErrorsViewed());
	}, []);

	const content = !errors.length ? (
		<Alert severity="info">{t("errorLog.empty")}</Alert>
	) : (
		<SpacedGridContainer>
			{errors.map((v, i) => {
				const expanded = expandedIndex === i;
				const summary = v.message.length > 200 ? `${v.message.substring(0, 200)}...` : v.message;

				return (
					<SpacedGridItem key={i} maxCols={1}>
						<SelectCard onClick={() => setExpandedIndex(expanded ? null : i)}>
							<Grid size={12} sx={{ alignItems: "center", display: "flex", justifyContent: "space-between" }}>
								<BodyTextSmall color="text.secondary">{timestampFormatDateTime(Number(v.timestamp))}</BodyTextSmall>
								<Icon sx={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 150ms" }}>
									expand_more
								</Icon>
							</Grid>

							{v.code && (
								<Grid size={12}>
									<SubtitleText bold>{v.code}</SubtitleText>
								</Grid>
							)}

							<Grid size={12}>
								<BodyTextSmall color="text.secondary">{summary}</BodyTextSmall>
							</Grid>
						</SelectCard>

						<Collapse in={expanded} unmountOnExit>
							<Divider sx={{ mb: 1 }} />
							<ErrorDetailView error={v} />
						</Collapse>
					</SpacedGridItem>
				);
			})}
		</SpacedGridContainer>
	);

	return (
		<Paper elevation={0} sx={{ p: 3 }}>
			{!hosted && (
				<TitleText bold sx={{ mb: 3 }}>
					{t("errorLog.title")}
				</TitleText>
			)}
			{content}
		</Paper>
	);
};

export default ErrorLog;
